import { useState, useRef, useEffect, memo, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../hooks/AuthContext";
import { ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import noSearchRecords from "../../assets/svg/no-matching-records.svg";
import { useNavigate } from "react-router";
import SearchCategoryList from "./SearchCategoryList";
import useApiQuery from "../../hooks/useApiQuery";
import {
  seachPlaceholder,
  getProductsForUniversalSearch,
} from "../../utilities/service";

const SearchBar = () => {
  const theme = useTheme();
  const { data } = useApiQuery({
    queryKey: ["seachPlaceholder"],
    queryFn: () => seachPlaceholder(),
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchState, setSearchState] = useState("empty");
  const [filteredData, setFilteredData] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [index, setIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width:599px)");
  const is600to800 = useMediaQuery("(min-width:600px) and (max-width:799px)");
  const is800to1000 = useMediaQuery("(min-width:800px) and (max-width:999px)");
  const isAbove1000 = useMediaQuery("(min-width:1000px)");

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  // ✅ Own API call — same endpoint as mobile app
  const { data: searchApiData } = useApiQuery({
    queryKey: ["universalSearch", user?.region_id],
    queryFn: () => getProductsForUniversalSearch(user?.region_id),
    enabled: !!user?.region_id,
  });

  const allProducts = searchApiData?.data?.products || [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSearchText("");
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setHighlightedIndex(-1);

    if (value.trim() === "") {
      setSearchState("empty");
      setFilteredData([]);
      return;
    }

    // ✅ Search from universal API data
    const matched = allProducts.filter((product) =>
      product.name?.toLowerCase().includes(value?.toLowerCase()),
    );

    if (matched.length > 0) {
      setSearchState("suggestions");
      setFilteredData(matched);
    } else {
      setSearchState("no-results");
      setFilteredData([]);
    }

    setIsDropdownOpen(true);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSearchState("empty");
    setFilteredData([]);
  };

  const handleFocus = () => {
    if (searchText.trim() === "") setSearchState("empty");
    setIsDropdownOpen(true);
  };

  const handleRecentSearchSelect = (itemText) => {
    const foundItem = allProducts.find((product) => {
      const name = product?.name || product?.product_name || "";
      return name.toLowerCase() === itemText.toLowerCase();
    });

    if (foundItem) {
      handleSelectSuggestion(foundItem);
    }
  };

  const handleSelectSuggestion = (item) => {
    const text = item?.name || item?.product_name;

    setRecentSearches((prev) => {
      const filtered = prev.filter(
        (p) => p.toLowerCase() !== text.toLowerCase(),
      );
      return [text, ...filtered].slice(0, 5); // max 5 items, no duplicates
    });

    if (item?.type === "P") {
      const productName = item?.product_name || item?.name || "product";
      const slug = productName.toLowerCase().replace(/\s+/g, "-");
      navigate(`/product-description/${slug}/${item?.id}`);
    } else {
      const slug = item?.slug || item?.name?.toLowerCase().replace(/\s+/g, "-");
      navigate(`/listing/${slug}`);
    }

    handleClearSearch();
    setIsDropdownOpen(false);
  };

  const keyboardOptions = useMemo(() => {
    if (searchState === "suggestions") {
      return filteredData.map((item) => ({
        key: `${item?.id ?? item?.name}-${item?.type ?? "suggestion"}`,
        action: () => handleSelectSuggestion(item),
      }));
    }

    if (searchState === "empty") {
      return recentSearches.map((item, idx) => ({
        key: `${item}-${idx}`,
        action: () => handleRecentSearchSelect(item),
      }));
    }

    return [];
  }, [searchState, filteredData, recentSearches, allProducts]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setHighlightedIndex(-1);
      return;
    }

    if (keyboardOptions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    if (highlightedIndex >= keyboardOptions.length) {
      setHighlightedIndex(0);
    }
  }, [isDropdownOpen, keyboardOptions, highlightedIndex]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleInputKeyDown = (e) => {
    if (!isDropdownOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setIsDropdownOpen(true);
      if (keyboardOptions.length > 0) {
        setHighlightedIndex(e.key === "ArrowDown" ? 0 : keyboardOptions.length - 1);
      }
      return;
    }

    if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (keyboardOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % keyboardOptions.length);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev <= 0 ? keyboardOptions.length - 1 : prev - 1,
      );
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const selected =
        highlightedIndex >= 0 ? keyboardOptions[highlightedIndex] : keyboardOptions[0];
      selected?.action();
    }
  };

  if (isMobile) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { sm: "350px", md: "600px" },
        margin: "0 auto",
        marginRight: { xs: "5px", sm: "15px" },
      }}
    >
      <TextField
        fullWidth
        placeholder={
          is600to800
            ? "Search"
            : is800to1000
              ? "Search"
              : isAbove1000
                ? "Search here"
                : "Search"
        }
        value={searchText}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        onKeyDown={handleInputKeyDown}
        variant="outlined"
        autoComplete="off"
        inputRef={inputRef}
        sx={{
          backgroundColor: "#fbfbfb",
          borderRadius: "999px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "999px",
            height: "50px",
            background: "rgba(255,255,255,0.8)",
            position: "relative",
            boxShadow: "inset 0 0 0 1px rgba(22, 22, 22, 0.04)",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(183,123,26,0.4)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(183,123,26,0.7)",
              borderWidth: "1.5px",
            },
            "& input": {
              position: "relative",
              zIndex: 2,
              fontSize: "15px",
              color: "var(--neutral-black)",
            },
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {searchText ? (
                <IconButton
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  sx={{ color: "var(--neutral-600)" }}
                >
                  <CloseIcon />
                </IconButton>
              ) : (
                <MagnifyingGlass
                  size={24}
                  style={{ color: "var(--neutral-600)" }}
                />
              )}
            </InputAdornment>
          ),
        }}
      />

      {isDropdownOpen && (
        <Box
          ref={dropdownRef}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 10,
            mt: 1,
            p: 3,
            maxHeight: "400px",      // ← add this
            overflowY: "auto",       // ← add this
          }}
        >
          {searchState === "empty" && (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  mb: 2,
                  textAlign: "left",
                  color: "var(--neutral-black)",
                }}
              >
                Recent search
              </Typography>
              {recentSearches.length > 0 ? (
                recentSearches.map((item, index) => (
                  <Box
                    component="button"
                    key={index}
                    onClick={() => handleRecentSearchSelect(item)}
                    aria-label={`Search for ${item}`}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      py: 1,
                      px: 2,
                      borderRadius: "10px",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                      backgroundColor:
                        highlightedIndex === index ? "rgba(0,0,0,0.06)" : "transparent",
                      // mb:0.5,
                      border:0,
                    }}
                  >
                    <ArrowCounterClockwise
                      size={20}
                      style={{
                        marginRight: "16px",
                        color: "var(--neutral-600)",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        m: "auto",
                        ml: 1,
                        mb: 0,
                        textAlign: "left",
                        lineHeight: "157%",
                        color: "var(--neutral-800)",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", color: "text.secondary", mb: 2 }}
                >
                  "No recent search has found"
                </Typography>
              )}
              <Box
                sx={{
                  height: 8,
                  backgroundColor: "#f9f8f8",
                  borderRadius: 16,
                  mt: 2,
                }}
              />
            </>
          )}

          {searchState === "suggestions" && (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  mb: 2,
                  textAlign: "left",
                  color: "var(--neutral-black)",
                }}
              >
                Related search
              </Typography>
              <FilteredList
                filteredData={filteredData}
                searchText={searchText}
                handleSelectSuggestion={handleSelectSuggestion}
                highlightedIndex={highlightedIndex}
                optionRefs={optionRefs}
              />
            </>
          )}

          {searchState === "no-results" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3,
              }}
            >
              <Box>
                <img
                  src={noSearchRecords}
                  alt="No Records"
                  style={{ width: "80%" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  textAlign: "center",
                  color: "var(--neutral-700)",
                  mt: 2,
                }}
              >
                Sorry, no results found for{" "}
                <Typography
                  variant="span"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    textAlign: "center",
                    color: "var(--neutral-black)",
                  }}
                >
                  "{searchText}"
                </Typography>
              </Typography>
            </Box>
          )}

          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mt: 2, mb: 1 }}
          >
            Popular search
          </Typography>
          <SearchCategoryList setIsDropdownOpen={setIsDropdownOpen} />
        </Box>
      )}
    </Box>
  );
};

export default memo(SearchBar);

export const FilteredList = ({
  filteredData,
  searchText,
  handleSelectSuggestion,
  highlightedIndex,
  optionRefs,
}) => {
  return filteredData?.map((item, index) => {
    const name = item?.name || "";
    const searchIndex = name.toLowerCase().indexOf(searchText.toLowerCase());

    let beforeMatch = "",
      matchText = "",
      afterMatch = "";

    if (searchIndex !== -1) {
      beforeMatch = name.substring(0, searchIndex);
      matchText = name.substring(searchIndex, searchIndex + searchText.length);
      afterMatch = name.substring(searchIndex + searchText.length);
    }

    return (
      <Box
        component="button"
        key={index}
        onClick={() => handleSelectSuggestion(item)}
        aria-label={`Search for ${item?.name}`}
        ref={(el) => {
          optionRefs.current[index] = el;
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          py: 1,
          px: 2,
          borderRadius: "10px",
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
          backgroundColor:
            highlightedIndex === index ? "rgba(0,0,0,0.06)" : "transparent",
          // mb:0.5,
          border:0,
        }}
      >
        <MagnifyingGlass
          size={20}
          style={{ marginRight: "16px", color: "var(--neutral-600)" }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            textAlign: "left",
            color: "var(--neutral-600)",
            lineHeight: "157%",
            textTransform: "capitalize",
            mb: 0,
          }}
        >
          {beforeMatch}
          <Box
            component="span"
            sx={{ fontWeight: 700, color: "var(--neutral-black)" }}
          >
            {matchText}
          </Box>
          {afterMatch}
        </Typography>
      </Box>
    );
  });
};
